import { action } from 'mobx';
import { omit } from 'lodash';
import moment from 'moment';
import Model from 'core/model/Model';
import { fromNow } from 'core/util/dateUtils';
import $dictionary from 'app/stores/$dictionary';
import $auth from 'app/stores/$auth';

class RunbookModel extends Model {
  get urlRoot() {
    return '/api/ui/runbook';
  }

  get timeAgo() {
    return fromNow(this.get('cdate'));
  }

  get lastModifiedTime() {
    const cdate = this.get('cdate');
    return cdate && moment(cdate).isValid() ? moment(cdate).format('MMM D, YYYY - h:mm A') : '';
  }

  get lastModifiedComment() {
    return this.get('comment') || '';
  }

  get isPreset() {
    return (
      !$auth.isPresetCompany && String(this.get('company_id')) === String($dictionary.dictionary.templateDashboardsCID)
    );
  }

  get revisionId() {
    return Number(this.get('revision_id'));
  }

  getMappingsOfType(resourceType) {
    const mappings = this.get('mappings');
    if (!mappings) {
      return [];
    }
    const types = Array.isArray(resourceType) ? resourceType : [resourceType];
    return mappings.filter((mapping) => types.includes(mapping.resource_type));
  }

  getMappingResourceTypes() {
    const mappings = this.get('mappings');
    if (!mappings) {
      return [];
    }
    const uniqueTypes = new Set();
    for (const mapping of mappings) {
      if (mapping.resource_type) {
        uniqueTypes.add(mapping.resource_type);
      }
    }
    return Array.from(uniqueTypes);
  }

  getMappingCounts() {
    const mappings = this.get('mappings');
    if (!mappings) {
      return {};
    }

    const mappingCounts = {};
    for (const mapping of mappings) {
      if (mapping.resource_type) {
        mappingCounts[mapping.resource_type] = (mappingCounts[mapping.resource_type] || 0) + 1;
      }
    }
    return mappingCounts;
  }

  hasMappingsOfType(resourceType) {
    return this.getMappingsOfType(resourceType).length > 0;
  }

  get revisionAuthor() {
    const user = this.get('user');
    return user?.user_full_name || '';
  }

  toString() {
    const name = this.get('name');
    const description = this.get('description') || '';
    const instructions = this.get('display_md') || '';

    return `Name: ${name}\n\nDescription: ${description}\n\n${instructions}`;
  }

  destroy(options = {}) {
    const hasMappings = this.get('mappings')?.length > 0;
    const data = options.data || {};

    return super.destroy({
      ...options,
      data: { force_delete: hasMappings, ...data }
    });
  }

  @action
  duplicate = (options = {}) => {
    const { removeId = true, save = true, deserialize = true } = options;
    const fieldsToOmit = removeId ? ['id', 'mappings'] : ['mappings'];
    const attributes = omit(this.get(), fieldsToOmit);

    if (!this.collection) {
      return new this.constructor(attributes);
    }

    const dupe = this.collection.build(attributes, { deserialize });

    if (save) {
      dupe.save(attributes);
    }

    return dupe;
  };
}

export default RunbookModel;
